import { useState } from 'react'
import {
  Layers,
  Plus,
  Server,
  Globe,
  CheckCircle2,
  Package,
  GitBranch,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react'
import styles from './AppInAKSPage.module.css'

type AppStatus = 'running' | 'pending' | 'failed' | 'updating'

interface DeployedApp {
  id: string
  name: string
  cluster: string
  namespace: string
  helmRelease: string
  imageTag: string
  status: AppStatus
  version: string
  replicas: string
  deployedAt: string
}

const CLUSTERS = [
  'aks-prod-eastus',
  'aks-prod-westus',
  'aks-demo-eastus',
  'aks-dev-eastus',
]

const NAMESPACES = ['aptean-prod', 'aptean-demo', 'aptean-dev', 'monitoring', 'ingress-nginx', 'cert-manager']

const HELM_CHARTS = ['ssp-api', 'ssp-ui', 'pay-edi', 'm2m-app', 'appcentral', 'genai-service', 'agent-central']

const MOCK_APPS: DeployedApp[] = [
  { id: 'app-001', name: 'ssp-api',        cluster: 'aks-prod-eastus',    namespace: 'aptean-prod',    helmRelease: 'ssp-api-v3',     imageTag: 'v2026.04.09', status: 'running',  version: '3.2.1', replicas: '3/3', deployedAt: '2026-04-09 08:12' },
  { id: 'app-002', name: 'ssp-ui',         cluster: 'aks-prod-eastus',    namespace: 'aptean-prod',    helmRelease: 'ssp-ui-v2',      imageTag: 'v2026.04.09', status: 'running',  version: '2.1.0', replicas: '2/2', deployedAt: '2026-04-09 08:14' },
  { id: 'app-003', name: 'pay-edi',        cluster: 'aks-prod-eastus',    namespace: 'aptean-prod',    helmRelease: 'pay-edi-v5',     imageTag: 'v2026.04.07', status: 'running',  version: '5.0.3', replicas: '3/3', deployedAt: '2026-04-07 14:30' },
  { id: 'app-004', name: 'appcentral',     cluster: 'aks-prod-westus',    namespace: 'aptean-prod',    helmRelease: 'appcentral-v8',  imageTag: 'v2026.04.05', status: 'running',  version: '8.1.2', replicas: '4/4', deployedAt: '2026-04-05 11:00' },
  { id: 'app-005', name: 'genai-service',  cluster: 'aks-demo-eastus', namespace: 'aptean-demo', helmRelease: 'genai-v1',       imageTag: 'v2026.04.08', status: 'running',  version: '1.4.0', replicas: '2/2', deployedAt: '2026-04-08 09:50' },
  { id: 'app-006', name: 'm2m-app',        cluster: 'aks-demo-eastus', namespace: 'aptean-demo', helmRelease: 'm2m-v4',         imageTag: 'v2026.04.02', status: 'failed',   version: '4.2.0', replicas: '0/2', deployedAt: '2026-04-02 16:20' },
  { id: 'app-007', name: 'agent-central',  cluster: 'aks-dev-eastus',     namespace: 'aptean-dev',     helmRelease: 'agent-v2',       imageTag: 'v2026.04.09', status: 'updating', version: '2.0.0', replicas: '1/2', deployedAt: '2026-04-09 10:05' },
  { id: 'app-008', name: 'ssp-api',        cluster: 'aks-demo-eastus', namespace: 'aptean-demo', helmRelease: 'ssp-api-demo',imageTag: 'v2026.04.09', status: 'running',  version: '3.2.2', replicas: '2/2', deployedAt: '2026-04-09 09:00' },
]

const STATUS_CONFIG: Record<AppStatus, { label: string; cls: string }> = {
  running:  { label: 'Running',  cls: 'statusRunning'  },
  pending:  { label: 'Pending',  cls: 'statusPending'  },
  failed:   { label: 'Failed',   cls: 'statusFailed'   },
  updating: { label: 'Updating', cls: 'statusUpdating' },
}

interface DeployForm {
  cluster: string
  appName: string
  namespace: string
  helmChart: string
  imageTag: string
  replicas: number
  environment: string
}

export function AppInAKSPage() {
  const [form, setForm] = useState<DeployForm>({
    cluster: CLUSTERS[0],
    appName: '',
    namespace: NAMESPACES[0],
    helmChart: HELM_CHARTS[0],
    imageTag: '',
    replicas: 2,
    environment: 'production',
  })
  const [clusterFilter, setClusterFilter] = useState('all')

  const handleFormChange = (field: keyof DeployForm, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const filteredApps = clusterFilter === 'all'
    ? MOCK_APPS
    : MOCK_APPS.filter((a) => a.cluster === clusterFilter)

  const stats = {
    clusters: CLUSTERS.length,
    apps: MOCK_APPS.length,
    namespaces: new Set(MOCK_APPS.map((a) => a.namespace)).size,
    helmToday: MOCK_APPS.filter((a) => a.deployedAt.startsWith('2026-04-09')).length,
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <Layers size={20} />
          </div>
          <div>
            <h1 className={styles.pageTitle}>New App in Existing AKS</h1>
            <p className={styles.pageSubtitle}>Deploy applications into existing Kubernetes clusters via Helm</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--dc-deploy-bg)', color: 'var(--dc-deploy)' }}>
            <Server size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{stats.clusters}</div>
            <div className={styles.statLabel}>AKS Clusters</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--aptean-success-bg)', color: 'var(--aptean-success)' }}>
            <Package size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{stats.apps}</div>
            <div className={styles.statLabel}>Deployed Apps</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--dc-infra-bg)', color: 'var(--dc-infra)' }}>
            <Globe size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{stats.namespaces}</div>
            <div className={styles.statLabel}>Namespaces</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--dc-ops-bg)', color: 'var(--dc-ops)' }}>
            <GitBranch size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{stats.helmToday}</div>
            <div className={styles.statLabel}>Helm Releases Today</div>
          </div>
        </div>
      </div>

      {/* Split Layout */}
      <div className={styles.splitLayout}>
        {/* Deploy Form (left) */}
        <div className={styles.formCard}>
          <div className={styles.formCardHeader}>
            <Plus size={16} />
            <span>Deploy Application</span>
          </div>
          <div className={styles.formBody}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Target Cluster</label>
              <select
                className={styles.formSelect}
                value={form.cluster}
                onChange={(e) => handleFormChange('cluster', e.target.value)}
              >
                {CLUSTERS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Application Name</label>
              <input
                className={styles.formInput}
                placeholder="e.g. pay-edi"
                value={form.appName}
                onChange={(e) => handleFormChange('appName', e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Namespace</label>
              <select
                className={styles.formSelect}
                value={form.namespace}
                onChange={(e) => handleFormChange('namespace', e.target.value)}
              >
                {NAMESPACES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Helm Chart</label>
              <select
                className={styles.formSelect}
                value={form.helmChart}
                onChange={(e) => handleFormChange('helmChart', e.target.value)}
              >
                {HELM_CHARTS.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Image Tag</label>
              <input
                className={styles.formInput}
                placeholder="e.g. v2026.04.09"
                value={form.imageTag}
                onChange={(e) => handleFormChange('imageTag', e.target.value)}
              />
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Replicas</label>
                <input
                  className={styles.formInput}
                  type="number"
                  min={1}
                  max={20}
                  value={form.replicas}
                  onChange={(e) => handleFormChange('replicas', parseInt(e.target.value) || 1)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Environment</label>
                <select
                  className={styles.formSelect}
                  value={form.environment}
                  onChange={(e) => handleFormChange('environment', e.target.value)}
                >
                  <option value="dev">Development</option>
                  <option value="demo">Demo</option>
                  <option value="production">Production</option>
                  <option value="uat_usgov">USGOV UAT</option>
                  <option value="prod_usgov">USGOV Prod</option>
                </select>
              </div>
            </div>
            <button className={styles.deployBtn}>
              <Layers size={15} />
              Deploy to AKS
            </button>
          </div>
        </div>

        {/* Apps Table (right) */}
        <div className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <span className={styles.tableTitle}>Deployed Applications</span>
            <select
              className={styles.clusterFilter}
              value={clusterFilter}
              onChange={(e) => setClusterFilter(e.target.value)}
            >
              <option value="all">All Clusters</option>
              {CLUSTERS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>App</th>
                  <th>Cluster</th>
                  <th>Namespace</th>
                  <th>Status</th>
                  <th>Version</th>
                  <th>Pods</th>
                  <th>Deployed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApps.map((app) => {
                  const cfg = STATUS_CONFIG[app.status]
                  return (
                    <tr key={app.id} className={styles.tableRow}>
                      <td>
                        <div className={styles.appName}>{app.name}</div>
                        <div className={styles.appHelm}>{app.helmRelease}</div>
                      </td>
                      <td className={styles.cellMono}>{app.cluster}</td>
                      <td>
                        <span className={styles.nsBadge}>{app.namespace}</span>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[cfg.cls]}`}>
                          {app.status === 'running'  && <CheckCircle2 size={10} />}
                          {app.status === 'failed'   && <AlertTriangle size={10} />}
                          {app.status === 'updating' && <RefreshCw size={10} className={styles.spinning} />}
                          {cfg.label}
                        </span>
                      </td>
                      <td>
                        <span className={styles.versionTag}>{app.imageTag}</span>
                      </td>
                      <td className={styles.cellMuted}>{app.replicas}</td>
                      <td className={styles.cellMuted}>{app.deployedAt}</td>
                      <td>
                        <button className={styles.actionBtn}>
                          <RefreshCw size={12} />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
